import { ApolloError } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { CSSProperties, useContext, useMemo, useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { useBatch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { CARD_UPDATE_MODAL_ANCHOR_NAME } from '@shared/scenes/cards/form/update/cardUpdateModalAnchorName';
import { CardNavigationSubtable } from '@shared/scenes/cards/navigation';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { useViewTemplate } from '@shared/scenes/workflows/hooks';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { SUBTABLES, useFeatureFlag } from '@shared/util/featureFlags';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { AddMoreButton } from '../AddMoreButton';
import { EditFieldContainer } from '../EditFieldContainer';
import { SubtableList } from './SubtableList';
import { SubtableRowMenu } from './SubtableRowMenu';
import { SubtableTable } from './SubtableTable';
import { SubtableTableModal } from './SubtableTableModal';
import { useSubtableBuilderLink } from './subtableBuilderLink';
import { getSubtableColumns } from './subtableColumns';
import {
  SubtableDrafts,
  SubtableDraftsContext,
  SubtableFieldDrafts,
  useDraftSubtableRows,
} from './subtableDrafts';
import { useSubtableRecurrenceAlert } from './subtableRecurrence';
import { SubtableRouteContext } from './subtableRoute';
import { SUBTABLE_ROW_LIMIT, useSubtableRowLimitAlert } from './subtableRowLimit';
import { RowMenuRoute, SubtableRowMenuState, useSubtableRowMenu } from './useSubtableRowMenu';
import { SubtableRows, useSubtableRows } from './useSubtableRows';
import styles from './SubtableEditField.module.css';

interface Props {
  component: API.SubtableComponent;
  /** The card that owns the rows; unset on the Create form, which holds them as drafts. */
  entityId?: string;
  /** The card's template, which owns this field. */
  workflowTemplateId: string;
  /** False on a public external form: row forms hide and limit fields as the card form does. */
  isAuthenticated?: boolean;
  style?: CSSProperties;
}

/**
 * The rows this card owns in a subtable. On the Create form, rows are drafts the form creates once
 * the card exists (see SubtableDraftsContext); without that context, as in a new row's own form,
 * the field isn't rendered.
 */
export function SubtableEditField({ entityId, ...props }: Props) {
  const isSubtablesEnabled = useFeatureFlag(SUBTABLES);
  const formType = useContext(CardFormTypeContext);
  const drafts = useContext(SubtableDraftsContext);

  if (!isSubtablesEnabled) {
    return null;
  }
  if (formType === 'CREATE') {
    return isNil(drafts) ? null : <DraftSubtableEditField {...props} drafts={drafts} />;
  }
  if (isNil(entityId)) {
    return null;
  }
  return <SavedSubtableEditField {...props} entityId={entityId} />;
}

type ContentProps = Omit<Props, 'entityId'>;

function SavedSubtableEditField({ entityId, ...props }: ContentProps & { entityId: string }) {
  const subtableRows = useSubtableRows(props.component, entityId);
  return (
    <SubtableEditFieldContent {...props} subtableRows={subtableRows} parentEntityId={entityId} />
  );
}

function DraftSubtableEditField({ drafts, ...props }: ContentProps & { drafts: SubtableDrafts }) {
  const { subtableRows, fieldDrafts } = useDraftSubtableRows(props.component, drafts);
  return <SubtableEditFieldContent {...props} subtableRows={subtableRows} drafts={fieldDrafts} />;
}

function SubtableEditFieldContent({
  component,
  workflowTemplateId,
  isAuthenticated = true,
  style,
  subtableRows,
  parentEntityId,
  drafts,
}: ContentProps & {
  subtableRows: SubtableRows;
  /** The saved card the rows belong to. */
  parentEntityId?: string;
  /** The Create form's draft rows for this field, in place of parentEntityId. */
  drafts?: SubtableFieldDrafts;
}) {
  const { rows, refs, totalResults, hasMore, loadMore, refetch, loading, error, workflowTemplate } =
    subtableRows;
  const configureSubtable = useSubtableBuilderLink(component, workflowTemplateId);
  const isDesktop = useIsDesktop();
  const { rowMenu, modalRowMenu, showSubtableModal, setShowSubtableModal, openModalNewRow } =
    useSubtableData(component.id, rows);
  const openSubtableModal = () => setShowSubtableModal(true);
  const showRowLimitAlert = useSubtableRowLimitAlert();
  const isAtRowLimit = totalResults >= SUBTABLE_ROW_LIMIT;
  // Mobile adds rows in the expanded view: open it with its new-row sheet already up, so the new
  // row lands in the full list rather than the card form's short preview.
  // A card being created with a repeat can't have rows, so adding one says why instead.
  const { shouldBatchCreate } = useBatch();
  const isRepeating = !isNil(drafts) && shouldBatchCreate;
  const showRecurrenceAlert = useSubtableRecurrenceAlert();
  const addFirstRow = () => {
    if (isRepeating) {
      void showRecurrenceAlert('addRow');
    } else {
      rowMenu.openNew();
    }
  };
  const addRow = () => {
    if (isRepeating) {
      void showRecurrenceAlert('addRow');
    } else if (isAtRowLimit) {
      void showRowLimitAlert();
    } else if (isDesktop) {
      rowMenu.openNew();
    } else {
      openModalNewRow();
    }
  };
  const addModalRow = () => {
    if (isRepeating) {
      void showRecurrenceAlert('addRow');
    } else if (isAtRowLimit) {
      void showRowLimitAlert();
    } else {
      modalRowMenu.openNew();
    }
  };
  // Columns track the Update view template, when someone has customized one: wait for it, so
  // columns it hides don't flash in first.
  const updateViewTemplateId = workflowTemplate?.viewTemplateDefaults?.update;
  const { viewTemplate: updateViewTemplate, loading: updateViewTemplateLoading } = useViewTemplate(
    workflowTemplate?.id,
    updateViewTemplateId,
  );
  const isColumnsLoading = !isNil(updateViewTemplateId) && updateViewTemplateLoading;
  const columns = useMemo(() => {
    return getSubtableColumns(workflowTemplate, updateViewTemplate);
  }, [workflowTemplate, updateViewTemplate]);
  const hasNoVisibleFields = !isColumnsLoading && isEmpty(columns);
  const hasError = !isNil(error);
  // The spinner and the placeholder never show together: an error shows instead of the spinner,
  // and the empty state waits for the columns as well as the rows.
  const isInitialLoad = !hasError && ((loading && isEmpty(rows)) || isColumnsLoading);
  const isEmptyState = !loading && !isColumnsLoading && !hasError && isEmpty(rows);
  // With no columns to show, any rows the subtable has stay hidden behind the empty state.
  const isNoVisibleFieldsState = !loading && !hasError && hasNoVisibleFields;
  const showRows = !isEmpty(rows) && !isColumnsLoading && !hasNoVisibleFields;
  // Read-only from the field's own setting or a view template override, or because the whole card
  // is read-only (e.g. a public external form), which stamps readonly onto every component.
  const isReadOnly = !!component.readonly;
  const isForbidden = isForbiddenError(error);
  const placeholder = getPlaceholder({ hasError, isForbidden, hasNoVisibleFields, isReadOnly });
  const showPlaceholder = isEmptyState || isNoVisibleFieldsState || hasError;
  const label = getLabelWithCount(component, hasError || hasNoVisibleFields ? 0 : totalResults);
  // With no visible fields, the new row menu explains why rows can't be added.
  const shouldShowNewRowMenu = (isEmptyState || isNoVisibleFieldsState) && !isReadOnly;

  const tableProps = {
    workflowTemplateId: workflowTemplate?.id ?? '',
    columns,
    rows,
    refs,
    hasMore,
    onLoadMore: loadMore,
  };

  return (
    <>
      <EditFieldContainer
        component={component}
        label={label}
        showIcon={showPlaceholder}
        showPlaceholder={showPlaceholder}
        placeholder={placeholder}
        largerInput={false}
        style={style}
        isReadOnly={isReadOnly}
        onPress={shouldShowNewRowMenu ? addFirstRow : undefined}
        footer={
          showRows &&
          !isReadOnly && (
            <View className={styles.footer}>
              <SubtableAddRowButton component={component} onPress={addRow} />
            </View>
          )
        }
      >
        {isInitialLoad && <LoadingIndicator />}
        {showRows && (
          <View className={styles.tableFrame}>
            {isDesktop && (
              <SubtableTable
                {...tableProps}
                selectedRowId={rowMenu.selectedRowId}
                onRowPress={rowMenu.openRow}
                isReadOnly={isReadOnly}
              />
            )}
            {!isDesktop && (
              <SubtableList
                workflowTemplateId={tableProps.workflowTemplateId}
                columns={columns}
                rows={rows}
                refs={refs}
                totalCount={totalResults}
                onShowMore={openSubtableModal}
                selectedRowId={rowMenu.selectedRowId}
                onRowPress={rowMenu.openRow}
                isReadOnly={isReadOnly}
              />
            )}
            {/* Mobile opens the expanded view from "Show N more" and Add row instead. */}
            {isDesktop && (
              <Button
                variant="ghost"
                size="medium"
                icon="arrows-expand"
                iconOnly
                label={i18n('common.expand')}
                tooltip={i18n('common.expand')}
                className={styles.expandButton}
                onClick={openSubtableModal}
              />
            )}
          </View>
        )}
      </EditFieldContainer>
      {/* Render modals and overlays next to the field, not inside it, to avoid inheriting focus-visible styles. */}
      {!isEmpty(rows) && !hasNoVisibleFields && (
        <SubtableTableModal
          {...tableProps}
          component={component}
          workflowTemplate={workflowTemplate}
          parentEntityId={parentEntityId}
          drafts={drafts}
          isAuthenticated={isAuthenticated}
          onRowSaved={refetch}
          title={label ?? getComponentLabel(component) ?? ''}
          isReadOnly={isReadOnly}
          isOpen={showSubtableModal}
          onOpenChange={setShowSubtableModal}
          rowMenu={modalRowMenu}
          onAddRow={addModalRow}
        />
      )}
      <SubtableRowMenu
        workflowTemplate={workflowTemplate}
        parentEntityId={parentEntityId}
        drafts={drafts}
        isAuthenticated={isAuthenticated}
        isOpen={rowMenu.isOpen}
        row={rowMenu.row}
        isReadOnly={isReadOnly}
        hasNoVisibleFields={hasNoVisibleFields}
        isColumnsLoading={isColumnsLoading}
        onConfigure={configureSubtable}
        containerAnchorName={CARD_UPDATE_MODAL_ANCHOR_NAME}
        onSaved={refetch}
        onClose={rowMenu.close}
      />
    </>
  );
}

/**
 * The field's row menu, its expanded modal, and the modal's own row menu (see SubtableTableModal's
 * rowMenu prop), all from one route: the card form's subtable routes, when the URL names this
 * field's subtable, or the same state held here where the form has none (another host). Opening the
 * modal closes the field's row menu, since the modal's route has no field row in it.
 */
function useSubtableData(
  componentId: string,
  rows: API.WorkflowEntityFragment[],
): {
  rowMenu: SubtableRowMenuState;
  modalRowMenu: SubtableRowMenuState;
  showSubtableModal: boolean;
  setShowSubtableModal: (isOpen: boolean) => void;
  /** Opens the modal with its new row form already up. */
  openModalNewRow: () => void;
} {
  const subtableRoute = useContext(SubtableRouteContext);
  const [localSubtable, setLocalSubtable] = useState<CardNavigationSubtable>();
  const { subtable: routedSubtable, navigate } = subtableRoute ?? {
    subtable: localSubtable,
    navigate: setLocalSubtable,
  };
  const subtable = routedSubtable?.componentId === componentId ? routedSubtable : undefined;
  const rowIds = useMemo(() => rows.map(row => row.id), [rows]);

  // The row menu of the field (expanded false) or the modal (expanded true). Closing the modal's
  // leaves the modal open.
  const getRowMenuRoute = (expanded: boolean): RowMenuRoute => ({
    target: subtable?.expanded === expanded ? subtable.row : undefined,
    open: row => navigate({ componentId, expanded, row }),
    close: () => navigate(expanded ? { componentId, expanded } : undefined),
    rowIds,
  });

  return {
    rowMenu: useSubtableRowMenu(getRowMenuRoute(false)),
    modalRowMenu: useSubtableRowMenu(getRowMenuRoute(true)),
    showSubtableModal: subtable?.expanded === true,
    setShowSubtableModal: isOpen => navigate(isOpen ? { componentId, expanded: true } : undefined),
    openModalNewRow: () => navigate({ componentId, expanded: true, row: { type: 'new' } }),
  };
}

/** "+ Add row" under the field's rows; opens the add-row menu. */
function SubtableAddRowButton({
  component,
  onPress,
}: {
  component: API.SubtableComponent;
  onPress: () => void;
}) {
  return (
    <AddMoreButton
      component={component}
      label={i18n('card.blocks.subtableInput.addRow')}
      onPress={onPress}
    />
  );
}

function getPlaceholder({
  hasError,
  isForbidden,
  hasNoVisibleFields,
  isReadOnly,
}: {
  hasError: boolean;
  isForbidden: boolean;
  hasNoVisibleFields: boolean;
  isReadOnly: boolean;
}): string {
  if (isForbidden) {
    return i18n('card.blocks.subtableInput.noAccess');
  }
  if (hasError) {
    return i18n('card.blocks.subtableInput.loadError');
  }
  if (hasNoVisibleFields) {
    return i18n('card.blocks.subtableInput.noVisibleFields');
  }
  if (isReadOnly) {
    return i18n('card.blocks.subtableInput.noRows');
  }
  return i18n('card.blocks.subtableInput.addFirstRow');
}

/** "Line Items (3)" once there are rows, like Related Card fields; the plain label otherwise. */
function getLabelWithCount(component: API.SubtableComponent, count: number): string | undefined {
  if (count > 0) {
    return `${getComponentLabel(component)} (${count})`.trim();
  }
  return undefined;
}

/**
 * Whether the backend refused the rows for this member's access, e.g. Personal Access, which grants
 * no subtable rows even on cards the member can see.
 */
function isForbiddenError(error: Error | undefined): boolean {
  if (!(error instanceof ApolloError)) {
    return false;
  }
  return error.graphQLErrors.some(graphQLError => {
    const coastApiError = graphQLError.extensions?.coastApiError as { code?: string } | undefined;
    return coastApiError?.code === 'FORBIDDEN';
  });
}
